import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Lenis from 'lenis';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/context/AuthContext';
import { CartProvider } from '@/context/CartContext';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';

import Home from '@/pages/Home';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ProductListing from '@/pages/ProductListing';
import ProductDetail from '@/pages/ProductDetail';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import Orders from '@/pages/Orders';
import Profile from '@/pages/Profile';
import CustomArt from '@/pages/CustomArt';
import OurStory from '@/pages/OurStory';
import Contact from '@/pages/Contact';
import FAQ from '@/pages/FAQ';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';

import '@/App.css';

// Smooth-scroll to top on every route change for a clean page transition.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    // Initialize Lenis smooth scroll
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <div className="App">
            <ScrollToTop />
            <Navigation />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              
              <Route
                path="/featured"
                element={
                  <ProductListing
                    category="featured"
                    title="Featured Artwork"
                    description="Curated collection of signature and best-selling pieces"
                  />
                }
              />
              <Route
                path="/ocean"
                element={
                  <ProductListing
                    category="ocean"
                    title="Ocean Series"
                    description="Ocean-inspired resin artwork with wave depth and motion"
                    bgColor="#E0E8E9"
                  />
                }
              />
              <Route
                path="/sports-cars"
                element={
                  <ProductListing
                    category="sports-cars"
                    title="Sports & Cars"
                    description="Automotive-inspired wall art and resin pieces"
                    bgColor="#F5F5F0"
                  />
                }
              />
              <Route
                path="/coasters"
                element={
                  <ProductListing
                    category="coasters"
                    title="Coasters & Mini Frames"
                    description="Small-format resin art and giftable pieces"
                  />
                }
              />
              
              <Route path="/product/:productId" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/story" element={<OurStory />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
            </Routes>
            <Footer />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: '14px',
                },
              }}
            />
          </div>
        </CartProvider>
      </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
